import { UntypedFormControl } from '@angular/forms';
import { Component, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Observable, Subject, forkJoin, combineLatest } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { SystemConfigurationHttpService } from '../../services/system-configuration-http.service';
import { SystemConfigurationService } from '../../services/system-configuration.service';
import { SystemConfigModel } from '../../models/system-configuration-model';
import { first, takeWhile } from 'rxjs';
import { RouteContextService } from '../../../context/services/route-context.service';
import { UserSettingsService } from '../../../user-settings/services/user-settings.service';
import { ProductService } from '../../../common/services/product.service';
import { MojoWellKnownNames } from '@common/constants/mojo-well-known-names';
import { OverviewService } from '@feature/overview/services/overview.service';
import { SelectItem } from 'primeng/api';
import { IOverviewTileModel } from '@feature/overview/models/overview-tile-model';
import { IOverviewTilesModel } from '@feature/overview/models/overview-tiles-model';
import { GeneralInformation } from '@admin/models/system-configuration-model';
import { OverviewHttpService } from '@feature/overview/services/overview-http.service';
import { ProductSpecificServiceProvider } from '@feature/product/services/product-specific-service-provider.service';
import { ListerConfigurationService } from '@admin/services/lister-configuration.service';
import {UserPreferencesHttpService} from "@feature/user-preferences/services/user-preferences-http.service";
import {UserPreferencesData} from "@feature/user-preferences/models/user-preferences-data";
import { HttpResponse } from '@angular/common/http';

@Component({
  selector: 'mojo-system-configuration-page',
  templateUrl: './system-configuration-page.component.html'
})
export class SystemConfigurationPageComponent implements OnInit, OnDestroy {

  title$: Observable<string>;
  isAlive: boolean = true;
  loading$: Observable<boolean>;

  allTiles: IOverviewTileModel[];
  selectableTiles: SelectItem[] = [];

  productName: string;

  private readonly loadingSubject: Subject<boolean>;

  pristineModel: SystemConfigModel;

  configForm: UntypedFormGroup;

  constructor(
    private readonly systemConfigurationHttpService: SystemConfigurationHttpService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly messageService: ToastrService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly routeContextService: RouteContextService,
    private readonly systemConfigurationService: SystemConfigurationService,
    private readonly userSettingsService: UserSettingsService,
    private readonly overviewService: OverviewService,
    private readonly overviewHttpService: OverviewHttpService,
    private readonly listerConfigurationService: ListerConfigurationService,
    private router: Router,
    productService: ProductService,
    private readonly productServiceProviderService: ProductSpecificServiceProvider,
    private readonly userPreferencesService: UserPreferencesHttpService) {

    this.loadingSubject = new Subject<boolean>();
    this.loading$ = this.loadingSubject.asObservable();
    this.productName = this.routeContextService.getProductFromRouteData(this.activatedRoute);
    this.title$ = productService.currentProductTitle('System Settings', this.productName);
    this.createForm();
  }

  createForm() {
    this.configForm = this.formBuilder.group({
      BrandInformation: this.formBuilder.group({
        applicationName: ['', [Validators.required, Validators.maxLength(150)]],
        supportTelNo: ['', [Validators.required, Validators.maxLength(100)]],
        emailSignature: ['', [Validators.required, Validators.maxLength(250)]],
        supportEmailAddress: ['', [Validators.maxLength(150)]],
        companyWebSite: ['', [Validators.maxLength(150)]],
      }),
      OverviewConfig: this.formBuilder.group({
        selectedTiles: null,
        selectedFilterField: null,
        selectedFilterFieldItems: []
      }),
      ListerConfiguration: this.formBuilder.group({
        config: new UntypedFormControl([])
      })
    });
  }

  resetForm() {
    this.configForm.setValue(this.pristineModel);
  }

  cancel() {
    const adminRoute = this.productServiceProviderService.getServiceByProduct(this.productName).adminDashboardRoute;
    this.router.navigate([adminRoute]);
  }

  ngOnInit() {
    this.loadingSubject.next(true);

    forkJoin([
      this.systemConfigurationHttpService.getSystemConfiguration(this.productName),
      this.overviewService.getOverviewTilesByProductName(this.productName),
      this.userPreferencesService.getUserPreferencesSystemDefaults()]
    ).pipe(
      first(),
      takeWhile(() => this.isAlive)
    ).subscribe(([brandInfo, overviewTiles, systemDefaults]: [GeneralInformation, IOverviewTilesModel, UserPreferencesData]) => {

      const config = new SystemConfigModel();
      config.BrandInformation = brandInfo;

      // create a list of selectable overview items
      this.allTiles = overviewTiles.AllTiles;

      // set preselected overview items
      this.selectableTiles = this.mapToSelectItemModel(this.allTiles);
      config.OverviewConfig = {
        selectedTiles: this.selectableTiles.filter(t =>
          overviewTiles.AvailableTiles.map(t2 => t2.Name).includes(t.value)).map(t => t.value),
          selectedFilterField: overviewTiles.SelectedFilterField,
          selectedFilterFieldItems: overviewTiles.SelectedFilterFieldItems
      };

      config.ListerConfiguration = {
        config: []
      }
      this.pristineModel = config;
      this.configForm.setValue(config);
      this.loadingSubject.next(false);
    });
  }

  submit() {
    const formModel: SystemConfigModel = this.configForm.value;
    const selectedTiles: IOverviewTileModel[] = this.allTiles.filter(t => this.configForm.value.OverviewConfig.selectedTiles.includes(t.Name));

    const updateSystemConfiguration = this.systemConfigurationHttpService.updateSystemConfiguration(this.productName, formModel.BrandInformation);
    const saveOverviewConfiguration = this.overviewHttpService.updateOverviewConfiguration({
        AvailableTiles: selectedTiles,
        Product: this.productName
      });
    const saveListerConfiguration = this.listerConfigurationService.saveListerConfiguration(this.configForm.value.ListerConfiguration.config, this.productName);
    const getCurrentProduct = this.userSettingsService.getCurrentProduct();

    combineLatest([updateSystemConfiguration, saveOverviewConfiguration, getCurrentProduct, saveListerConfiguration]).pipe(
      takeWhile(() => this.isAlive)
    ).subscribe(([updateSystemConfigResult, saveOverviewConfigResult, product, saveListerConfigurationResult]) => {
      this.messageService.success('System config updated', 'Changes saved');
      this.configForm.markAsPristine();
      this.changeDetector.markForCheck();
      this.systemConfigurationService.loadGeneralInformation(product);
    }, () => {
      this.messageService.error('An error occurred, changes not saved', 'Warning');
      this.changeDetector.markForCheck();
    });
  }

  ngOnDestroy() {
    this.isAlive = false;
  }

  showOverviewConfigurationSettings(): boolean {
    return this.productName !== MojoWellKnownNames.Products.MojoForSuppliers && this.productName !== MojoWellKnownNames.Products.MojoForTenants;
  }

  showDocumentCentreSettings(): boolean {
    return this.productName !== MojoWellKnownNames.Products.MojoForSuppliers && this.productName !== MojoWellKnownNames.Products.MojoForTenants;
  }

  mapToSelectItemModel(items: IOverviewTileModel[]): SelectItem[] {
    const results: SelectItem[] = [];
    items.forEach(t2 => results.push({ label: t2.Label, value: t2.Name }));
    return results;
  }
}
